import isFiniteNumber from 'lodash/fp/isFinite';
import isInteger from 'lodash/fp/isInteger';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { Unit } from '../../common/constants';

const useParseLocaleNumber = (intl: IntlShape, fractionDigits: number) => {
    const thousandSeparator = intl.formatNumber(1111).replace(/1/g, '');
    const decimalSeparator = intl.formatNumber(1.1).replace(/1/g, '');

    return (localeNumberString: string) => {
        let normalizedString = thousandSeparator
            ? localeNumberString.replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
            : localeNumberString;
        normalizedString = decimalSeparator
            ? normalizedString.replace(new RegExp(`\\${decimalSeparator}`), '.')
            : normalizedString;

        return Number(Number(normalizedString).toFixed(fractionDigits));
    };
};

export const LabeledNumberInput = (props: LabeledNumberInputProps) => {
    const {
        className = '',
        labelId,
        labelAddon,
        value = 0,
        integerOnly = false,
        onChange = () => {},
        unit,
        unitIcon,
        iconLeft,
        id,
        disabled,
        maxValue,
        errorMessage,
    } = props;

    const intl = useIntl();
    const parseLocaleNumber = useParseLocaleNumber(intl, 2);

    const formattedValue = intl.formatNumber(value);

    const [displayedValue, setDisplayedValue] = useState<string>(formattedValue);

    useEffect(() => {
        if (parseLocaleNumber(displayedValue) !== value) {
            setDisplayedValue(formattedValue);
        }
    }, [parseLocaleNumber, displayedValue, value, formattedValue]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isValid = (val: number) => isFiniteNumber(val) && val >= 0 && (!integerOnly || isInteger(val));

        let newValueString = event.target.value;
        let newValue = parseLocaleNumber(newValueString);

        if (unit === Unit.pct && newValue < 0) {
            newValue = 0;
            newValueString = '0';
        }

        if (unit === Unit.pct && newValue > 100) {
            newValue = 100;
            newValueString = '100';
        }

        if (isValid(newValue)) {
            setDisplayedValue(newValueString);
            onChange(newValue);
        }
    };

    const handleInputBlur = () => {
        if (displayedValue !== formattedValue) {
            setDisplayedValue(formattedValue);
        }
    };

    const hasExceededMax = maxValue !== undefined && value > maxValue;

    return (
        <div className={`form-group ${className} ${hasExceededMax ? 'has-error' : ''}`} data-testid={id}>
            {labelId && (
                <label className='control-label'>
                    <FormattedMessage id={labelId} />
                    {labelAddon}
                </label>
            )}
            <div className='input-group'>
                {iconLeft && (
                    <span className='input-group-addon'>
                        <span className={`rioglyph ${iconLeft}`} />
                    </span>
                )}
                <input
                    className='form-control'
                    inputMode='decimal'
                    value={displayedValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    min={unit === Unit.pct ? 0 : undefined}
                    max={unit === Unit.pct ? 100 : maxValue}
                    disabled={disabled}
                />
                {unit && (
                    <div data-testid={`unit-label=${unit}`} className='input-group-addon'>
                        {unit}
                    </div>
                )}
                {unitIcon && (
                    <div className='input-group-addon text-color-gray padding-right-10'>
                        <span className={`rioglyph rioglyph-${unitIcon} text-size-20`} aria-hidden='true' />
                    </div>
                )}
            </div>
            {hasExceededMax && errorMessage && <span className='help-block text-color-danger'>{errorMessage}</span>}
        </div>
    );
};

interface LabeledNumberInputProps {
    disabled?: boolean;
    className?: string;
    labelId?: string;
    labelAddon?: React.ReactNode;
    value?: number;
    integerOnly?: boolean;
    onChange?: (value?: number) => void;
    unit?: string;
    unitIcon?: string;
    iconLeft?: string;
    id?: string;
    maxValue?: number;
    errorMessage?: string;
}
